// src/app/modules/Settings/OrganizationTab.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import {
  Building2, MapPin, Phone, Mail, Globe, Edit3, Plus, Trash2,
  Star, StarOff, CheckCircle, AlertCircle, Loader, X, Save,
  ExternalLink, Info, ChevronDown, ChevronUp, Clock, Calendar,
} from 'lucide-react';
import { toast } from 'react-toastify';
import * as authActions from '../../Auth/_redux/authRedux';
import AddressAutocomplete from '../../Auth/components/AddressAutocomplete';
import { useFeatureGate } from '../../Dashbord/components/FeatureGate';

import {
  updateOrganization,
  getOrganizationLocations,
  addOrganizationLocation,
  updateOrganizationLocation,
  removeOrganizationLocation,
  setPrimaryLocation as setPrimaryLocationAPI,
} from '../../Auth/_redux/authCrud';

const COUNTRIES = [
  { code: 'NG', name: 'Nigeria' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'KE', name: 'Kenya' },
  { code: 'GH', name: 'Ghana' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'ET', name: 'Ethiopia' },
  { code: 'TZ', name: 'Tanzania' },
  { code: 'UG', name: 'Uganda' },
  { code: 'RW', name: 'Rwanda' },
  { code: 'SN', name: 'Senegal' },
  { code: 'CI', name: "Côte d'Ivoire" },
  { code: 'CM', name: 'Cameroon' },
  { code: 'EG', name: 'Egypt' },
  { code: 'MA', name: 'Morocco' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'IN', name: 'India' },
  { code: 'BR', name: 'Brazil' },
  { code: 'SG', name: 'Singapore' },
  { code: 'ZM', name: 'Zambia' },
  { code: 'ZW', name: 'Zimbabwe' },
].sort((a, b) => a.name.localeCompare(b.name));

const DAYS = [
  { key: 'Monday',    short: 'Mon' },
  { key: 'Tuesday',   short: 'Tue' },
  { key: 'Wednesday', short: 'Wed' },
  { key: 'Thursday',  short: 'Thu' },
  { key: 'Friday',    short: 'Fri' },
  { key: 'Saturday',  short: 'Sat' },
  { key: 'Sunday',    short: 'Sun' },
];

const DEFAULT_BIZ = {
  allow_remote_work: false,
  work_start_time:   '09:00',
  work_end_time:     '17:00',
  work_days:         ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
  timezone_mode:     'individual',
  shifts:            [],
};

const ORG_TYPE_LABELS = {
  residential_estate:   'Residential Estate',
  office_building:      'Office Building',
  commercial_building:  'Commercial Building',
  residential_building: 'Residential Building',
  event_venue:          'Event Venue',
  estate:               'Estate',
  corporate:            'Corporate',
  school:               'School',
  hospital:             'Hospital',
  hotel:                'Hotel',
  retail:               'Retail',
  government:           'Government',
  other:                'Other',
};

// ─── LocationModal ────────────────────────────────────────────────────────────

function LocationModal({ location, onSave, onClose, isSaving }) {
  const isEdit = !!location?.location_id;

  const [name,  setName]  = useState(location?.name  || '');
  const [phone, setPhone] = useState(location?.phone || '');
  const [email, setEmail] = useState(location?.email || '');
  const [notes, setNotes] = useState(location?.notes || '');

  const [addressValues, setAddressValues] = useState({
    street: location?.address?.street || '',
    city:   location?.address?.city   || '',
    state:  location?.address?.state  || '',
  });

  const [touched,     setTouched]     = useState({});
  const [coordinates, setCoordinates] = useState(
    location?.address?.coordinates?.length === 2
      ? { lat: location.address.coordinates[1], lon: location.address.coordinates[0] }
      : null
  );
  const [errors, setErrors] = useState({});

  const formikShim = {
    values:  addressValues,
    touched,
    errors:  {},
    setFieldValue: (field, value) => {
      setAddressValues(prev => ({ ...prev, [field]: value }));
      setCoordinates(null);
    },
    getFieldProps: (field) => ({
      name:    field,
      value:   addressValues[field] || '',
      onChange: (e) => {
        setAddressValues(prev => ({ ...prev, [field]: e.target.value }));
        setCoordinates(null);
      },
      onBlur: () => setTouched(prev => ({ ...prev, [field]: true })),
    }),
    handleBlur: (e) => setTouched(prev => ({ ...prev, [e.target.name]: true })),
  };

  const handleAddressSelect = (data) => {
    if (!data) { setCoordinates(null); return; }
    if (data.coordinates) setCoordinates(data.coordinates);
    if (data.address) {
      setAddressValues({
        street: data.address.street || '',
        city:   data.address.city   || '',
        state:  data.address.state  || '',
      });
    }
  };

  const validate = () => {
    const e = {};
    if (!name.trim())               e.name   = 'Location name is required';
    if (!addressValues.street.trim()) e.street = 'Street address is required';
    if (!addressValues.city.trim())   e.city   = 'City is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSave({
      name, phone, email, notes,
      address: {
        street: addressValues.street,
        city:   addressValues.city,
        state:  addressValues.state,
        ...(coordinates ? { coordinates: [coordinates.lon, coordinates.lat] } : {}),
      },
    });
  };

  return (
    <div className="sty-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sty-dialog">
        <div className="sty-modal__header">
          <h5>{isEdit ? 'Edit location' : 'Add location'}</h5>
          <button className="sty-modal__close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="sty-modal__body">
          {/* Name */}
          <div className="sty-field">
            <label>Location name <span className="sty-required">*</span></label>
            <input
              type="text"
              className={`sty-input ${errors.name ? 'sty-input--error' : ''}`}
              placeholder="e.g. Main Gate, Block A, Branch Lagos"
              value={name}
              onChange={e => setName(e.target.value)}
            />
            {errors.name && <span className="sty-field__error">{errors.name}</span>}
          </div>

          {/* Address autocomplete */}
          <div className="sty-field">
            <label>Address <span className="sty-required">*</span></label>
            <p className="sty-field__hint" style={{ marginBottom: 6 }}>Search or enter manually</p>
            <AddressAutocomplete
              formik={formikShim}
              onAddressSelect={handleAddressSelect}
              placeholder="Search for address…"
            />
            {(errors.street || errors.city) && (
              <span className="sty-field__error">Street and city are required</span>
            )}
          </div>

          {/* Contact row */}
          <div className="sty-field-grid sty-field-grid--2">
            <div className="sty-field">
              <label>Phone (optional)</label>
              <input type="tel" className="sty-input" placeholder="+1 555 0100" value={phone} onChange={e => setPhone(e.target.value)} />
            </div>
            <div className="sty-field">
              <label>Email (optional)</label>
              <input type="email" className="sty-input" placeholder="branch@…" value={email} onChange={e => setEmail(e.target.value)} />
            </div>
          </div>

          {/* Notes */}
          <div className="sty-field">
            <label>Notes (optional)</label>
            <textarea className="sty-input sty-input--textarea" rows={2} placeholder="Additional details…" value={notes} onChange={e => setNotes(e.target.value)} />
          </div>

          {/* Coordinate feedback */}
          {coordinates ? (
            <div className="sty-badge-row sty-badge-row--success">
              <CheckCircle size={13} /> Location verified on map
            </div>
          ) : (addressValues.street || addressValues.city) ? (
            <div className="sty-badge-row sty-badge-row--warning">
              <AlertCircle size={13} /> Select from suggestions to verify coordinates
            </div>
          ) : null}
        </div>

        <div className="sty-modal__footer">
          <button className="sty-btn sty-btn--ghost" onClick={onClose} disabled={isSaving}>Cancel</button>
          <button className="sty-btn sty-btn--primary" onClick={handleSubmit} disabled={isSaving}>
            {isSaving
              ? <><Loader size={13} className="sty-spin" /> Saving…</>
              : isEdit ? 'Update location' : 'Add location'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── DeleteConfirmModal ───────────────────────────────────────────────────────

function DeleteConfirmModal({ locationName, onConfirm, onClose, isDeleting }) {
  return (
    <div className="sty-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sty-dialog sty-dialog--sm" style={{ textAlign: 'center' }}>
        <div className="sty-danger-icon"><Trash2 size={22} /></div>
        <h5 style={{ fontSize: 16, fontWeight: 700, color: '#1C1C2E', margin: '0 0 8px' }}>Remove location</h5>
        <p style={{ fontSize: 13, color: '#7E8299', margin: '0 0 24px', lineHeight: 1.6 }}>
          Remove <strong>{locationName}</strong>? This cannot be undone.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button className="sty-btn sty-btn--ghost" onClick={onClose} disabled={isDeleting}>Cancel</button>
          <button className="sty-btn sty-btn--danger" onClick={onConfirm} disabled={isDeleting}>
            {isDeleting ? 'Removing…' : 'Remove'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── LocationCard ─────────────────────────────────────────────────────────────

function LocationCard({ location, onSetPrimary, onEdit, onDelete, canDelete }) {
  const addr = location.address || {};
  const addressStr = [addr.street, addr.city, addr.state].filter(Boolean).join(', ');

  return (
    <div className={`loc-card${location.is_primary ? ' loc-card--primary' : ''}`}>
      {/* Card header */}
      <div className="loc-card__head">
        <div className="loc-card__name-row">
          {location.is_primary && (
            <span className="loc-primary-badge"><Star size={10} /> Primary</span>
          )}
          <span className="loc-card__name">{location.name}</span>
        </div>
        <div className="loc-card__actions">
          {!location.is_primary && (
            <button className="loc-icon-btn" title="Set as primary" onClick={() => onSetPrimary(location.location_id)}>
              <StarOff size={14} />
            </button>
          )}
          <button className="loc-icon-btn" title="Edit" onClick={() => onEdit(location)}>
            <Edit3 size={14} />
          </button>
          {canDelete && !location.is_primary && (
            <button className="loc-icon-btn loc-icon-btn--danger" title="Remove" onClick={() => onDelete(location.location_id, location.name)}>
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Address */}
      {addressStr && (
        <div className="loc-card__addr">
          <MapPin size={12} />
          <span>{addressStr}</span>
        </div>
      )}

      {/* Contact */}
      {(location.phone || location.email) && (
        <div className="loc-card__contact">
          {location.phone && <span><Phone size={11} />{location.phone}</span>}
          {location.email && <span><Mail size={11} />{location.email}</span>}
        </div>
      )}

      {/* Verified */}
      {addr.coordinates?.length === 2 && (
        <div className="loc-card__verified">
          <CheckCircle size={11} /> Verified
        </div>
      )}
    </div>
  );
}

// ─── Main OrganizationTab ─────────────────────────────────────────────────────

export function OrganizationTab() {
  const dispatch = useDispatch();
  const { org } = useSelector((state) => ({ org: state.auth.organization }), shallowEqual);
  const { guardLimit } = useFeatureGate();

  const [editingInfo,   setEditingInfo]   = useState(false);
  const [infoForm,      setInfoForm]      = useState({ name: '', email: '', phone: '', description: '', website: '' });
  const [infoSaving,    setInfoSaving]    = useState(false);

  const [editingBiz,    setEditingBiz]    = useState(false);
  const [bizForm,       setBizForm]       = useState(DEFAULT_BIZ);
  const [bizSaving,     setBizSaving]     = useState(false);
  const [locations,     setLocations]     = useState([]);
  const [locsLoading,   setLocsLoading]   = useState(true);
  const [showLocModal,  setShowLocModal]  = useState(false);
  const [editingLoc,    setEditingLoc]    = useState(null);
  const [locSaving,     setLocSaving]     = useState(false);
  const [deleteTarget,  setDeleteTarget]  = useState(null);
  const [deleting,      setDeleting]      = useState(false);
  const [showLocs,      setShowLocs]      = useState(true);

  const isOnTrial = org?.subscription?.status === 'trial' || !org?.subscription?.status;

  useEffect(() => {
    if (org) {
      setInfoForm({
        name:         org.name         || '',
        email:        org.email        || '',
        phone:        org.phone        || org.phone_number || '',
        description:  org.description  || '',
        website:      org.website      || '',
        country_code: org.country_code || '',
      });
      const bs = org.business_settings || {};
      setBizForm({
        allow_remote_work: bs.allow_remote_work ?? DEFAULT_BIZ.allow_remote_work,
        work_start_time:   bs.work_start_time   || DEFAULT_BIZ.work_start_time,
        work_end_time:     bs.work_end_time     || DEFAULT_BIZ.work_end_time,
        work_days:         bs.work_days         || DEFAULT_BIZ.work_days,
        timezone_mode:     bs.timezone_mode     || DEFAULT_BIZ.timezone_mode,
        shifts:            bs.shifts            || DEFAULT_BIZ.shifts,
      });
    }
  }, [org]);

  useEffect(() => {
    if (org?.organization_id) fetchLocations();
  }, [org?.organization_id]);

  async function fetchLocations() {
    setLocsLoading(true);
    try {
      const res  = await getOrganizationLocations(org.organization_id);
      const locs = res.data.locations || [];
      setLocations(locs);
      dispatch(authActions.actions.locationsLoaded(locs));
    } catch {
      toast.error('Failed to load locations');
    } finally {
      setLocsLoading(false);
    }
  }

  async function handleInfoSave() {
    setInfoSaving(true);
    try {
      const payload = { ...infoForm };
      if (!payload.country_code) delete payload.country_code;
      const res = await updateOrganization(org.organization_id, payload);
      dispatch(authActions.actions.orgUpdated(res.data));
      toast.success('Organization updated');
      setEditingInfo(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update');
    } finally {
      setInfoSaving(false);
    }
  }

  const toggleDay = (day) => {
    setBizForm(p => {
      const days = p.work_days || [];
      return {
        ...p,
        work_days: days.includes(day)
          ? days.filter(d => d !== day)
          : [...days, day],
      };
    });
  };

  const addShift = () => {
    setBizForm(p => ({ ...p, shifts: [...(p.shifts || []), { name: '', start_time: '', end_time: '' }] }));
  };

  const removeShift = (idx) => {
    setBizForm(p => ({ ...p, shifts: (p.shifts || []).filter((_, i) => i !== idx) }));
  };

  const updateShift = (idx, field, value) => {
    setBizForm(p => ({
      ...p,
      shifts: (p.shifts || []).map((s, i) => i === idx ? { ...s, [field]: value } : s),
    }));
  };

  async function handleBizSave() {
    setBizSaving(true);
    try {
      const res = await updateOrganization(org.organization_id, { business_settings: bizForm });
      dispatch(authActions.actions.orgUpdated(res.data));
      toast.success('Business settings updated');
      setEditingBiz(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update');
    } finally {
      setBizSaving(false);
    }
  }

  async function handleAddLocationSave(data) {
    setLocSaving(true);
    try {
      const res = await addOrganizationLocation(org.organization_id, data);
      const loc = res.data.location;
      setLocations(prev => loc.is_primary ? [...prev.map(l => ({ ...l, is_primary: false })), loc] : [...prev, loc]);
      dispatch(authActions.actions.locationAdded(loc, res.data.address));
      toast.success('Location added');
      setShowLocModal(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to add location');
    } finally {
      setLocSaving(false);
    }
  }

  async function handleEditLocationSave(data) {
    setLocSaving(true);
    try {
      const res = await updateOrganizationLocation(org.organization_id, editingLoc.location_id, data);
      const loc = res.data.location;
      setLocations(prev => prev.map(l => l.location_id === editingLoc.location_id ? loc : l));
      dispatch(authActions.actions.locationUpdated(loc, res.data.address));
      toast.success('Location updated');
      setEditingLoc(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update location');
    } finally {
      setLocSaving(false);
    }
  }

  async function handleSetPrimary(locationId) {
    try {
      const res = await setPrimaryLocationAPI(org.organization_id, locationId);
      setLocations(prev => prev.map(l => ({ ...l, is_primary: l.location_id === locationId })));
      dispatch(authActions.actions.primaryLocationSet(locationId, res.data.address));
      toast.success('Primary location updated');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to set primary');
    }
  }

  async function handleDeleteConfirm() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await removeOrganizationLocation(org.organization_id, deleteTarget.id);
      setLocations(prev => prev.filter(l => l.location_id !== deleteTarget.id));
      dispatch(authActions.actions.locationRemoved(deleteTarget.id));
      toast.success('Location removed');
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to remove location');
    } finally {
      setDeleting(false);
    }
  }

  if (!org) return (
    <div style={{ padding: '60px 24px', textAlign: 'center', color: '#9CA3AF' }}>
      <Building2 size={36} style={{ opacity: 0.3, marginBottom: 10 }} />
      <p style={{ fontSize: 13 }}>No organization found.</p>
    </div>
  );

  return (
    <div className="org-tab">

      {/* ── Organization info ──────────────────────────────────────────── */}
      <div className="org-section">
        <div className="org-section__head">
          <div>
            <div className="org-section__title">Organization info</div>
            <div className="org-section__sub">Basic details visible across your workspace</div>
          </div>
          {!editingInfo ? (
            <button className="sty-btn sty-btn--ghost sty-btn--sm" onClick={() => setEditingInfo(true)}>
              <Edit3 size={13} /> Edit
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="sty-btn sty-btn--ghost sty-btn--sm" onClick={() => {
                setInfoForm({ name: org.name || '', email: org.email || '', phone: org.phone || org.phone_number || '', description: org.description || '', website: org.website || '', country_code: org.country_code || '' });
                setEditingInfo(false);
              }} disabled={infoSaving}>Cancel</button>
              <button className="sty-btn sty-btn--primary sty-btn--sm" onClick={handleInfoSave} disabled={infoSaving}>
                {infoSaving ? <><Loader size={12} className="sty-spin" /> Saving…</> : <><Save size={12} /> Save</>}
              </button>
            </div>
          )}
        </div>

        <div className="sty-field-grid">
          <div className="sty-field">
            <label>Organization name</label>
            {editingInfo
              ? <input className="sty-input" value={infoForm.name} onChange={e => setInfoForm(p => ({ ...p, name: e.target.value }))} />
              : <div className="sty-read-value">{org.name}</div>}
          </div>

          <div className="sty-field">
            <label>Type</label>
            <div className="sty-read-value">
              <span className="sty-type-badge">{ORG_TYPE_LABELS[org.type] || org.type}</span>
            </div>
          </div>

          <div className="sty-field">
            <label>Email</label>
            {editingInfo
              ? <input type="email" className="sty-input" value={infoForm.email} onChange={e => setInfoForm(p => ({ ...p, email: e.target.value }))} />
              : <div className="sty-read-value"><Mail size={13} className="sty-read-icon" />{org.email || <span className="sty-empty">Not set</span>}</div>}
          </div>

          <div className="sty-field">
            <label>Phone</label>
            {editingInfo
              ? <input type="tel" className="sty-input" value={infoForm.phone} onChange={e => setInfoForm(p => ({ ...p, phone: e.target.value }))} />
              : <div className="sty-read-value"><Phone size={13} className="sty-read-icon" />{org.phone || org.phone_number || <span className="sty-empty">Not set</span>}</div>}
          </div>

          <div className="sty-field">
            <label>Website</label>
            {editingInfo
              ? <input type="url" className="sty-input" placeholder="https://…" value={infoForm.website} onChange={e => setInfoForm(p => ({ ...p, website: e.target.value }))} />
              : <div className="sty-read-value">
                  <Globe size={13} className="sty-read-icon" />
                  {org.website
                    ? <a href={org.website} target="_blank" rel="noreferrer" className="sty-link">{org.website} <ExternalLink size={11} /></a>
                    : <span className="sty-empty">Not set</span>}
                </div>}
          </div>

          <div className="sty-field">
            <label>Country</label>
            {editingInfo
              ? (
                <select
                  className="sty-input"
                  value={infoForm.country_code}
                  onChange={e => setInfoForm(p => ({ ...p, country_code: e.target.value }))}
                >
                  <option value="">Select country…</option>
                  {COUNTRIES.map(c => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              )
              : <div className="sty-read-value">
                  <Globe size={13} className="sty-read-icon" />
                  {org.country_code
                    ? (COUNTRIES.find(c => c.code === org.country_code)?.name || org.country_code)
                    : <span className="sty-empty">Not set</span>}
                </div>}
          </div>

          <div className="sty-field sty-field--span2">
            <label>Description</label>
            {editingInfo
              ? <textarea className="sty-input sty-input--textarea" rows={3} value={infoForm.description} onChange={e => setInfoForm(p => ({ ...p, description: e.target.value }))} />
              : <div className="sty-read-value sty-read-value--text">{org.description || <span className="sty-empty">No description</span>}</div>}
          </div>
        </div>
      </div>

      {/* ── Business Settings ─────────────────────────────────────────── */}
      <div className="org-section">
        <div className="org-section__head">
          <div>
            <div className="org-section__title"><Clock size={14} /> Business Settings</div>
            <div className="org-section__sub">Work hours, remote policy, and timezone preferences</div>
          </div>
          {!editingBiz ? (
            <button className="sty-btn sty-btn--ghost sty-btn--sm" onClick={() => setEditingBiz(true)}>
              <Edit3 size={13} /> Edit
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="sty-btn sty-btn--ghost sty-btn--sm" onClick={() => {
                const bs = org.business_settings || {};
                setBizForm({
                  allow_remote_work: bs.allow_remote_work ?? DEFAULT_BIZ.allow_remote_work,
                  work_start_time:   bs.work_start_time   || DEFAULT_BIZ.work_start_time,
                  work_end_time:     bs.work_end_time     || DEFAULT_BIZ.work_end_time,
                  work_days:         bs.work_days         || DEFAULT_BIZ.work_days,
                  timezone_mode:     bs.timezone_mode     || DEFAULT_BIZ.timezone_mode,
                  shifts:            bs.shifts            || DEFAULT_BIZ.shifts,
                });
                setEditingBiz(false);
              }} disabled={bizSaving}>Cancel</button>
              <button className="sty-btn sty-btn--primary sty-btn--sm" onClick={handleBizSave} disabled={bizSaving}>
                {bizSaving ? <><Loader size={12} className="sty-spin" /> Saving…</> : <><Save size={12} /> Save</>}
              </button>
            </div>
          )}
        </div>

        {/* Remote work toggle */}
        <div style={{ borderBottom: '1px solid #F0F4F8', paddingBottom: 14, marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#1a2332', marginBottom: 2 }}>Allow remote work</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>Let staff clock in from locations outside registered sites</div>
            </div>
            {editingBiz ? (
              <div
                onClick={() => setBizForm(p => ({ ...p, allow_remote_work: !p.allow_remote_work }))}
                style={{
                  width: 40, height: 22, borderRadius: 11, position: 'relative', cursor: 'pointer',
                  background: bizForm.allow_remote_work ? '#5341C4' : '#D1D5DB',
                  transition: 'background 0.2s', flexShrink: 0,
                }}
              >
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', background: '#fff',
                  position: 'absolute', top: 2, transition: 'left 0.2s',
                  left: bizForm.allow_remote_work ? 20 : 2,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </div>
            ) : (
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 6,
                background: org.business_settings?.allow_remote_work ? '#F0FDF4' : '#F4F4F8',
                color:      org.business_settings?.allow_remote_work ? '#16a34a' : '#9CA3AF',
              }}>
                {org.business_settings?.allow_remote_work ? 'Enabled' : 'Disabled'}
              </span>
            )}
          </div>
        </div>

        {/* Work hours */}
        <div style={{ borderBottom: '1px solid #F0F4F8', paddingBottom: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>
            <Clock size={11} style={{ marginRight: 4 }} />Work Hours
          </div>
          <div className="sty-field-grid">
            <div className="sty-field">
              <label>Start time</label>
              {editingBiz
                ? <input type="time" className="sty-input" value={bizForm.work_start_time} onChange={e => { const v = e.target.value; setBizForm(p => ({ ...p, work_start_time: v })); }} />
                : <div className="sty-read-value"><Clock size={13} className="sty-read-icon" />{org.business_settings?.work_start_time || DEFAULT_BIZ.work_start_time}</div>
              }
            </div>
            <div className="sty-field">
              <label>End time</label>
              {editingBiz
                ? <input type="time" className="sty-input" value={bizForm.work_end_time} onChange={e => { const v = e.target.value; setBizForm(p => ({ ...p, work_end_time: v })); }} />
                : <div className="sty-read-value"><Clock size={13} className="sty-read-icon" />{org.business_settings?.work_end_time || DEFAULT_BIZ.work_end_time}</div>
              }
            </div>
          </div>
        </div>

        {/* Work days */}
        <div style={{ borderBottom: '1px solid #F0F4F8', paddingBottom: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>
            <Calendar size={11} style={{ marginRight: 4 }} />Work Days
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {DAYS.map(({ key, short }) => {
              const active = editingBiz ? (bizForm.work_days || []).includes(key) : (org.business_settings?.work_days || DEFAULT_BIZ.work_days).includes(key);
              return editingBiz ? (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleDay(key)}
                  style={{
                    padding: '5px 11px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    border: `1.5px solid ${active ? '#5341C4' : '#E5E7EB'}`,
                    background: active ? '#F0EEFF' : '#fff',
                    color: active ? '#5341C4' : '#9CA3AF',
                  }}
                >
                  {short}
                </button>
              ) : (
                <span
                  key={key}
                  style={{
                    padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                    background: active ? '#F0EEFF' : '#F4F4F8',
                    color:      active ? '#5341C4' : '#D1D5DB',
                  }}
                >
                  {short}
                </span>
              );
            })}
          </div>
        </div>

        {/* Timezone mode */}
        <div style={{ borderBottom: '1px solid #F0F4F8', paddingBottom: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>
            Timezone Mode
          </div>
          {editingBiz ? (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {[
                {
                  value: 'individual',
                  label: 'Per-staff timezone',
                  desc:  'Each employee is evaluated for lateness in their own local timezone. Best for distributed/remote teams.',
                },
                {
                  value: 'org',
                  label: 'Org timezone (strict)',
                  desc:  'All employees are evaluated against the organization\'s timezone. Best for single-location offices that need uniform enforcement.',
                },
              ].map(opt => (
                <label
                  key={opt.value}
                  style={{
                    flex: 1, minWidth: 200, display: 'flex', gap: 10, padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                    border: `1.5px solid ${bizForm.timezone_mode === opt.value ? '#5341C4' : '#E5E7EB'}`,
                    background: bizForm.timezone_mode === opt.value ? '#F0EEFF' : '#fff',
                  }}
                >
                  <input
                    type="radio"
                    name="timezone_mode"
                    value={opt.value}
                    checked={bizForm.timezone_mode === opt.value}
                    onChange={() => setBizForm(p => ({ ...p, timezone_mode: opt.value }))}
                    style={{ marginTop: 2, accentColor: '#5341C4', flexShrink: 0 }}
                  />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1a2332' }}>{opt.label}</div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, lineHeight: 1.5 }}>{opt.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          ) : (
            <div className="sty-read-value" style={{ width: 'fit-content' }}>
              {(org.business_settings?.timezone_mode || 'individual') === 'individual'
                ? 'Per-staff timezone'
                : 'Org timezone (strict)'}
            </div>
          )}
        </div>

        {/* Named shifts */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Named Shifts
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                {editingBiz
                  ? 'Leave empty for standard scheduling. Add for hospitals, factories, 24/7 operations.'
                  : (bizForm.shifts || []).length === 0
                    ? 'No named shifts — using standard hours'
                    : `${(org.business_settings?.shifts || []).length} shift schedule${(org.business_settings?.shifts || []).length !== 1 ? 's' : ''} configured`}
              </div>
            </div>
            {editingBiz && (
              <button
                type="button"
                className="sty-btn sty-btn--ghost sty-btn--sm"
                onClick={addShift}
              >
                <Plus size={12} /> Add shift
              </button>
            )}
          </div>

          {editingBiz ? (
            (bizForm.shifts || []).length === 0 ? (
              <div style={{ padding: '12px 0', color: '#94a3b8', fontSize: 12, fontStyle: 'italic' }}>
                No shifts added. Click "Add shift" for multi-shift orgs (e.g. Morning / Afternoon / Night).
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {/* Header row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 120px 32px', gap: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', paddingLeft: 10 }}>Shift Name</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>Start</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>End</div>
                  <div />
                </div>
                {(bizForm.shifts || []).map((sh, idx) => (
                  <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 120px 32px', gap: 8, alignItems: 'center' }}>
                    <input
                      type="text"
                      className="sty-input"
                      placeholder="e.g. Morning"
                      value={sh.name}
                      onChange={e => updateShift(idx, 'name', e.target.value)}
                    />
                    <input
                      type="time"
                      className="sty-input"
                      value={sh.start_time}
                      onChange={e => updateShift(idx, 'start_time', e.target.value)}
                    />
                    <input
                      type="time"
                      className="sty-input"
                      value={sh.end_time}
                      onChange={e => updateShift(idx, 'end_time', e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => removeShift(idx)}
                      style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid #F4D4D4', background: '#FEF2F2', color: '#DC2626', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                  <Info size={11} style={{ marginRight: 4 }} />
                  Times that cross midnight (e.g. Night 22:00–06:00) are handled automatically.
                </div>
              </div>
            )
          ) : (
            (org.business_settings?.shifts?.length > 0) ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {(org.business_settings.shifts).map((sh, i) => (
                  <div key={i} style={{ padding: '6px 12px', borderRadius: 8, background: '#F8F8FC', border: '1px solid #EDEDF3', fontSize: 12 }}>
                    <span style={{ fontWeight: 700, color: '#1a2332' }}>{sh.name}</span>
                    <span style={{ color: '#64748b', marginLeft: 6 }}>{sh.start_time}–{sh.end_time}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>No named shifts configured</div>
            )
          )}
        </div>
      </div>

      {/* ── Locations ──────────────────────────────────────────────────── */}
      <div className="org-section">
        <div className="org-section__head">
          <div>
            <div className="org-section__title">
              Locations
              <span className="org-count-pill">{locations.length}</span>
            </div>
            <div className="org-section__sub">
              Physical gates, branches, and access points
              {isOnTrial && (
                <span className="org-trial-note">
                  <Info size={11} /> Trial: 1 location. <a href="/settings?tab=billing">Upgrade</a> for more.
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="sty-btn sty-btn--ghost sty-btn--icon" onClick={() => setShowLocs(v => !v)}>
              {showLocs ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
            </button>
            <button className="sty-btn sty-btn--primary sty-btn--sm" onClick={() => guardLimit('max_spaces', locations.length, () => { setEditingLoc(null); setShowLocModal(true); })}>
              <Plus size={13} /> Add location
            </button>
          </div>
        </div>

        {showLocs && (
          <>
            {locsLoading ? (
              <div className="org-locs-empty">
                <Loader size={20} className="sty-spin" style={{ opacity: 0.4 }} />
                <span>Loading locations…</span>
              </div>
            ) : locations.length === 0 ? (
              <div className="org-locs-empty">
                <MapPin size={28} style={{ opacity: 0.2, marginBottom: 8 }} />
                <p>No locations added yet</p>
                <button className="sty-btn sty-btn--ghost sty-btn--sm" onClick={() => guardLimit('max_spaces', locations.length, () => { setEditingLoc(null); setShowLocModal(true); })}>
                  <Plus size={13} /> Add your first location
                </button>
              </div>
            ) : (
              <div className="org-locs-grid">
                {locations.map(loc => (
                  <LocationCard
                    key={loc.location_id}
                    location={loc}
                    onSetPrimary={handleSetPrimary}
                    onEdit={(l) => { setEditingLoc(l); setShowLocModal(false); }}
                    onDelete={(id, name) => setDeleteTarget({ id, name })}
                    canDelete={locations.length > 1}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Modals ─────────────────────────────────────────────────────── */}
      {showLocModal && (
        <LocationModal location={null} onSave={handleAddLocationSave} onClose={() => setShowLocModal(false)} isSaving={locSaving} />
      )}
      {editingLoc && (
        <LocationModal location={editingLoc} onSave={handleEditLocationSave} onClose={() => setEditingLoc(null)} isSaving={locSaving} />
      )}
      {deleteTarget && (
        <DeleteConfirmModal locationName={deleteTarget.name} onConfirm={handleDeleteConfirm} onClose={() => setDeleteTarget(null)} isDeleting={deleting} />
      )}
    </div>
  );
}

export default OrganizationTab;